import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME =
  "Bienvenido a Grupo Disimil. Soy el concierge del consorcio: puedo orientarte sobre Transporte Ejecutivo, Logística Deportiva, Protocolo VIP o Ciudad Garaje, y ayudarte a solicitar un presupuesto. ¿Qué necesitas hoy?";

const CHIPS = [
  { label: "¿Qué servicios ofrecen?", message: "¿Qué servicios ofrece Grupo Disimil?" },
  { label: "Solicitar presupuesto", message: "Quiero solicitar un presupuesto para mi empresa." },
  { label: "Ciudad Garaje", message: "Cuéntame sobre Ciudad Garaje." },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  const [sessionId] = useState(() => {
    const existing = localStorage.getItem("gd_chat_session");
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem("gd_chat_session", id);
    return id;
  });

  useEffect(() => {
    if (!open || messages.length > 0) return;
    fetch(`${API}/chat/history/${sessionId}`)
      .then((r) => r.json())
      .then((docs) => {
        if (Array.isArray(docs) && docs.length > 0) {
          setMessages(docs.map((d) => ({ role: d.role, content: d.content })));
        } else {
          setMessages([{ role: "assistant", content: WELCOME }]);
        }
      })
      .catch(() => setMessages([{ role: "assistant", content: WELCOME }]));
  }, [open, messages.length, sessionId]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const appendDelta = (delta) =>
    setMessages((s) => {
      const copy = [...s];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, content: last.content + delta };
      return copy;
    });

  const replaceLast = (content) =>
    setMessages((s) => {
      const copy = [...s];
      copy[copy.length - 1] = { role: "assistant", content };
      return copy;
    });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((s) => [...s, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat request failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const data = part.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) appendDelta(parsed.delta);
            if (parsed.error)
              replaceLast("Lo siento, tuve un inconveniente. Intenta de nuevo en unos segundos.");
          } catch {
            /* fragmento incompleto */
          }
        }
      }
    } catch {
      replaceLast("No pude conectar con el concierge. Verifica tu conexión e intenta de nuevo.");
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-btn"
        aria-label="Abrir chat con el concierge"
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-void shadow-[0_0_40px_-8px_rgba(212,175,55,0.7)] transition-transform duration-300 hover:scale-105"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.5, ease: EASE }}
      >
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-gold-light" />
          </span>
        )}
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            className="fixed bottom-24 right-4 z-[70] flex h-[560px] max-h-[72vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden border border-line bg-ink shadow-2xl sm:right-6"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            <div className="border-b border-line bg-void/60 px-5 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                Grupo Disimil
              </p>
              <p className="mt-1 font-serif text-xl font-semibold text-slate-100">
                Concierge Disimil
              </p>
              <p className="text-[11px] font-light text-slate-400">
                Te oriento y canalizo tu presupuesto
              </p>
            </div>

            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="chat-message-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm font-light leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-void"
                        : "border border-line bg-void/70 text-slate-200"
                    }`}
                    data-testid={m.role === "user" ? "chat-message-user" : "chat-message-assistant"}
                  >
                    {m.content ||
                      (m.role === "assistant" && streaming ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gold" />
                      ) : null)}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && !streaming && (
              <div className="flex flex-wrap gap-2 px-4 pb-3">
                {CHIPS.map((chip) => (
                  <button
                    key={chip.label}
                    onClick={() => send(chip.message)}
                    data-testid={`chat-chip-${chip.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="border border-gold/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors duration-300 hover:bg-gold hover:text-void"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-line bg-void/60 p-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu consulta..."
                data-testid="chat-input"
                className="flex-1 border border-line bg-void px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-gold/50 focus:outline-none"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                data-testid="chat-send-btn"
                aria-label="Enviar mensaje"
                className="flex h-11 w-11 items-center justify-center bg-gold text-void transition-opacity duration-300 hover:opacity-90 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
